/**
 * useHowToSeo - HowTo 结构化数据生成和注入
 * 
 * 功能：
 * 1. 为"如何使用"步骤生成 HowTo Schema.org 结构化数据
 * 2. 通过 useHead 注入结构化数据到页面
 */

import { computed, toValue, type MaybeRefOrGetter } from 'vue'
import { useHead } from '#app'

export interface HowToStep {
    title: string
    description: string
}

/**
 * 为"如何使用"步骤生成 HowTo 结构化数据并注入到页面
 * 
 * @param name HowTo 的名称（通常是页面标题或功能名称），支持响应式
 * @param steps 步骤数组，支持响应式
 */
export function useHowToSeo(
    name: MaybeRefOrGetter<string>,
    steps: MaybeRefOrGetter<HowToStep[]>
) {
    const howToData = computed(() => {
        const stepsValue = toValue(steps)
        const nameValue = toValue(name)
        
        if (!stepsValue || stepsValue.length === 0) {
            console.warn('No steps provided for HowTo schema')
            return null
        }

        return {
            '@context': 'https://schema.org',
            '@type': 'HowTo',
            name: nameValue,
            step: stepsValue.map((step: HowToStep, index: number) => ({
                '@type': 'HowToStep',
                position: index + 1,
                name: step.title,
                text: step.description,
            })),
        }
    })

    useHead({
        script: [
            {
                type: 'application/ld+json',
                innerHTML: computed(() => {
                    const data = howToData.value
                    return data ? JSON.stringify(data) : ''
                }),
            },
        ],
    })
}
